import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Combobox } from '../ui/combobox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Trash2, Search, Filter, X, Pencil } from 'lucide-react';
import apiClient from '../../utils/api';
import { toast } from 'sonner';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [columnFilters, setColumnFilters] = useState({ name: '', email: '', role: '' });
  
  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    role: '',
  });

  const [userData, setUserData] = useState({
    email: '',
    name: '',
    password: '',
    role: 'operator',
  });

  useEffect(() => {
    fetchUsers();
  }, [searchQuery]);

  useEffect(() => {
    applyFilters();
  }, [filters, allUsers]);

  const fetchUsers = async () => {
    try {
      const params = searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : '';
      const response = await apiClient.get(`/users${params}`);
      setAllUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...allUsers];
    
    if (filters.role) {
      filtered = filtered.filter(u => u.role === filters.role);
    }
    
    setUsers(filtered);
  };

  const handleOpenAddModal = () => {
    setEditingUser(null);
    setUserData({ email: '', name: '', password: '', role: 'operator' });
    setShowUserModal(true);
  };

  const handleOpenEditModal = (user) => {
    setEditingUser(user);
    setUserData({
      email: user.email,
      name: user.name,
      password: '', // Don't populate password for security
      role: user.role,
    });
    setShowUserModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (editingUser) {
      // Update existing user
      try {
        const updateData = {
          name: userData.name,
          email: userData.email,
          role: userData.role,
        };
        
        // Only include password if it was changed
        if (userData.password && userData.password.trim() !== '') {
          updateData.password = userData.password;
        }
        
        await apiClient.put(`/users/${editingUser.id}`, updateData);
        toast.success('User updated successfully');
        setShowUserModal(false);
        setEditingUser(null);
        fetchUsers();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to update user');
      }
    } else {
      // Create new user
      try {
        await apiClient.post('/auth/register', userData);
        toast.success('User created successfully');
        setShowUserModal(false);
        setUserData({ email: '', name: '', password: '', role: 'operator' });
        fetchUsers();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to create user');
      }
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await apiClient.delete(`/users/${userId}`);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const clearFilters = () => {
    setFilters({ role: '' });
    setSearchQuery('');
  };

  const hasActiveFilters = filters.role !== '' || searchQuery !== '';

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-primary font-semibold text-slate-900">User Management</h2>
        <Button
          data-testid="add-user-button"
          onClick={handleOpenAddModal}
          size="sm"
          className="h-7 px-2 text-xs flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800"
        >
          <Plus className="h-3.5 w-3.5" />
          Add User
        </Button>
      </div>

      <Card className="p-3 bg-white rounded-lg border border-slate-200">        {loading ? (
          <div className="text-center py-8 text-slate-600">Loading users...</div>
        ) : users.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            {hasActiveFilters ? 'No users match your filters' : 'No users found'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">
                    Name
                  </th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">
                    Email
                  </th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">
                    Role
                  </th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">
                    Actions
                  </th>
                </tr>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-2 py-1.5"><Input value={columnFilters.name} onChange={(e) => setColumnFilters(f => ({ ...f, name: e.target.value }))} placeholder="Filter..." className="h-7 text-xs" data-testid="user-filter-name" /></th>
                  <th className="px-2 py-1.5"><Input value={columnFilters.email} onChange={(e) => setColumnFilters(f => ({ ...f, email: e.target.value }))} placeholder="Filter..." className="h-7 text-xs" data-testid="user-filter-email" /></th>
                  <th className="px-2 py-1.5"><Input value={columnFilters.role} onChange={(e) => setColumnFilters(f => ({ ...f, role: e.target.value }))} placeholder="admin/manager..." className="h-7 text-xs" data-testid="user-filter-role" /></th>
                  <th className="px-2 py-1.5">
                    {(columnFilters.name || columnFilters.email || columnFilters.role) && (
                      <Button variant="ghost" size="sm" onClick={() => setColumnFilters({ name: '', email: '', role: '' })} className="h-7 px-2 text-xs" data-testid="user-filter-clear">
                        <X className="h-3.5 w-3.5 mr-1" />Clear
                      </Button>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.filter((u) => {
                  if (columnFilters.name && !(u.name || '').toLowerCase().includes(columnFilters.name.toLowerCase())) return false;
                  if (columnFilters.email && !(u.email || '').toLowerCase().includes(columnFilters.email.toLowerCase())) return false;
                  if (columnFilters.role && !(u.role || '').toLowerCase().includes(columnFilters.role.toLowerCase())) return false;
                  return true;
                }).map((user) => (
                  <tr key={user.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 text-sm font-medium text-slate-900">{user.name}</td>
                    <td className="py-3 px-4 text-sm text-slate-600">{user.email}</td>
                    <td className="py-3 px-4 text-sm">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium border ${
                          user.role === 'admin'
                            ? 'bg-purple-50 text-purple-700 border-purple-200'
                            : user.role === 'manager'
                            ? 'bg-blue-50 text-blue-700 border-blue-200'
                            : 'bg-slate-100 text-slate-600 border-slate-200'
                        }`}
                      >
                        {user.role.toUpperCase()}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1">
                        <Button
                          data-testid={`edit-user-${user.id}`}
                          onClick={() => handleOpenEditModal(user)}
                          variant="ghost"
                          size="sm"
                          className="text-slate-600 hover:text-slate-700 hover:bg-slate-100"
                          title="Edit User"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          data-testid={`delete-user-${user.id}`}
                          onClick={() => handleDeleteUser(user.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          title="Delete User"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Add/Edit User Modal */}
      <Dialog open={showUserModal} onOpenChange={(open) => { if (!open) { setEditingUser(null); } setShowUserModal(open); }}>
        <DialogContent data-testid="user-modal" className="bg-[#dcdcdc]">
          <DialogHeader>
            <DialogTitle className="text-lg font-primary font-semibold">
              {editingUser ? 'Edit User' : 'Add New User'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                data-testid="user-name-input"
                value={userData.name}
                onChange={(e) => setUserData({ ...userData, name: e.target.value })}
                required
                className="mt-1 h-10 bg-white"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                data-testid="user-email-input"
                type="email"
                value={userData.email}
                onChange={(e) => setUserData({ ...userData, email: e.target.value })}
                required
                className="mt-1 h-10 bg-white"
              />
            </div>
            <div>
              <Label>{editingUser ? 'New Password (leave blank to keep current)' : 'Password *'}</Label>
              <Input
                data-testid="user-password-input"
                type="password"
                value={userData.password}
                onChange={(e) => setUserData({ ...userData, password: e.target.value })}
                required={!editingUser}
                placeholder={editingUser ? 'Enter new password or leave blank' : ''}
                className="mt-1 h-10 bg-white"
              />
            </div>
            <div>
              <Label>Role *</Label>
              <Select
                value={userData.role}
                onValueChange={(value) => setUserData({ ...userData, role: value })}
              >
                <SelectTrigger data-testid="user-role-select" className="mt-1 h-10 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="operator">Operator</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              data-testid="submit-user-button"
              type="submit"
              className="w-full bg-slate-900 hover:bg-slate-800 h-10"
            >
              {editingUser ? 'Update User' : 'Add User'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
